/**
 *  A mapping to the actual css classes governing the colors. We hide it cause
 *  there is a difference in naming of the colors and limitations of where these
 *  should be used. Ideally, these mapping would be only used by components that
 *  allow for changing colors in a very limited manner that is 100% compatible
 *  with our Design System.
 */
export const colorsToFontColorClass = {
  "text/default": "text-gray-900",
  "text/primary": "text-blue-500",
  "text/primary/secondary": "text-blue-800",
  "text/primary/on-color": "text-white",
  "text/secondary": "text-gray-500",
  "text/tertiary": "text-gray-300",
  "text/success": "text-green-500",
  "text/success/secondary": "text-green-800",
  "text/warning": "text-yellow-500",
  "text/warning/secondary": "text-yellow-800",
  "text/error": "text-red-500",
  "text/error/secondary": "text-red-800",
  "text/purple": "text-purple-800",
} as const

/**
 *  Available colors that can be used across the software to define text colors.
 */
export type TextColor = keyof typeof colorsToFontColorClass

/**
 *  This is supposed to be an array of all colors that we use inside the software.
 *  The array is dictated by the Design System and serves as a base for the actual
 *  types, like the TextColor type and in future BackgroundColor and so on.
 *
 *  @see https://www.figma.com/design/mVCTjl16dXiTC880fA4GHT/Mijnpraktijk-design-system?node-id=5-2209&m=dev
 */
export const colors = Object.keys(colorsToFontColorClass) as TextColor[]

/**
 *  A mapping to the actual css classes governing the background colors. We hide it
 *  cause there is a difference in naming of the colors and limitations of where
 *  these should be used. Ideally, these mapping would be only used by components
 *  that allow for changing colors in a very limited manner that is 100% compatible
 *  with our Design System.
 */
export const colorsToBackgroundColorClass = {
  "background/default": "bg-white",
  "background/secondary": "bg-gray-50",
  "background/tertiary": "bg-gray-200",
  "background/disabled": "bg-gray-100",
  "background/active": "bg-gray-100",
  "background/primary": "bg-blue-500",
  "background/primary/hover": "bg-blue-700",
  "background/primary/disable": "bg-blue-200",
  "background/primary/subdue": "bg-blue-50",
  "background/success": "bg-green-500",
  "background/success/subdued": "bg-green-100",
  "background/success/subdued+": "bg-green-50",
  "background/warning": "bg-yellow-500",
  "background/warning/subdued": "bg-yellow-100",
  "background/warning/subdued+": "bg-yellow-50",
  "background/error": "bg-red-500",
  "background/error/subdued": "bg-red-100",
  "background/error/subdued+": "bg-red-50",
  "background/side menu": "bg-gray-800",
  "background/side menu/secondary": "bg-gray-900",
  "background/accent/blue": "bg-accent-blue",
  "background/accent/grey": "bg-accent-grey",
  "background/accent/yellow": "bg-accent-yellow",
  "background/accent/green": "bg-accent-green",
  "background/accent/orange": "bg-accent-orange",
  "background/accent/red": "bg-accent-red",
  "background/accent/pink": "bg-accent-pink",
  "background/accent/purple": "bg-accent-purple",
  "background/accent/lime": "bg-accent-lime",
  "background/accent/aqua": "bg-accent-aqua",
  "background/neutral": "bg-gray-500",
} as const

/**
 *  Available colors that can be used across the software to define background colors.
 */
export type BackgroundColor = keyof typeof colorsToBackgroundColorClass

/**
 *  This is an array of all background colors that we use in the software. The names
 *  come from the Design System and we should try to keep it up to date.
 *
 *  @see https://www.figma.com/design/mVCTjl16dXiTC880fA4GHT/Mijnpraktijk-design-system?node-id=5-2067&m=dev
 */
export const backgroundColors = Object.keys(colorsToBackgroundColorClass) as BackgroundColor[]

/**
 *  This type is an extension over the regular background that also includes
 *  "transparent" and "inherit" cases which aren't explicitly covered by our
 *  Design System but are common/implicit values in CSS.
 */
export type BackgroundDesignator = BackgroundColor | "transparent" | "inherit"

/**
 *  This type is an extension over the regular text that also includes
 *  "transparent" and "inherit" cases which aren't explicitly covered by our
 *  Design System but are common/implicit values in CSS.
 */
export type TextDesignator = TextColor | "transparent" | "inherit"

/**
 *  A mapping to the actual css classes governing the border shapes.
 */
export const borderShapeClasses = {
  "border/shape/4": "rounded",
  "border/shape/6": "rounded-md",
  "border/shape/8": "rounded-lg",
  "border/shape/12": "rounded-xl",
  "border/shape/16": "rounded-2xl",
  "border/shape/24": "rounded-3xl",
  "border/shape/rounded": "rounded-full",
}

/**
 *  The type of the border shapes that are available in the Design System.
 */
export type BorderShape = keyof typeof borderShapeClasses

/**
 *  A mapping to the actual css classes governing the border colors.
 */
export const colorsToBorderColorClass = {
  "border/default": "border-gray-100",
  "border/secondary": "border-gray-200",
  "border/primary": "border-blue-500",
  "border/success": "border-green-500",
  "border/warning": "border-yellow-500",
  "border/error": "border-red-500",
  "border/primary/secondary": "border-blue-200",
  "border/neutral": "border-gray-500",
} as const

/**
 *  Available colors that can be used across the software to define border colors.
 */
export type BorderColor = keyof typeof colorsToBorderColorClass

/**
 *  Border palette tuned for compact controls (For example, checkboxes and
 *  radio buttons).
 */
export const colorsToCompactBorderColorClass = {
  "border/default": "border-gray-300",
  "border/secondary": "border-gray-300",
  "border/primary": "border-blue-500",
  "border/success": "border-green-500",
  "border/warning": "border-yellow-500",
  "border/error": "border-red-500",
  "border/primary/secondary": "border-blue-200",
  "border/neutral": "border-gray-500",
} as const

/**
 *  Selects which border palette to use. `default` is the standard palette
 *  used by boxed controls; `compact` is the darker palette used by
 *  checkboxes and radio buttons.
 */
export type BorderDensity = "default" | "compact"

/**
 *  Available box shadow classes that can be used across the software.
 */
export const boxShadowClasses = {
  "shadow/base": "box-shadow-sm",
  "shadow/medium": "box-shadow-md",
  "shadow/large": "box-shadow-lg",
  "shadow/xlarge": "box-shadow-xl",
  "shadow/2xlarge": "box-shadow-2xl",
  "shadow/inner": "box-shadow-inner",
} as const

/**
 * A type describing the available box shadow classes.
 */
export type BoxShadow = keyof typeof boxShadowClasses
